import { B, fonts, Btn } from "../../../components/UI";

const formatDateFilterDisplay = (dateValue) => {
  if (!dateValue) return "";
  const [year, month, day] = dateValue.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

const getDateFilterMessage = (fromDisplay, toDisplay) => {
  if (!fromDisplay && !toDisplay) return "Showing quotations for all dates.";
  const parts = [];
  if (fromDisplay) parts.push(`from ${fromDisplay}`);
  if (toDisplay) parts.push(`to ${toDisplay}`);
  return `Showing quotations ${parts.join(" ")}.`;
};

// All quotations tab centralizes filters, list view, and quick actions (view/edit/delete/print).
export default function AllQuotationsTab({
  quotations,
  customers,
  designs,
  filteredQuotations,
  paginatedQuotations,
  quotationSearch,
  setQuotationSearch,
  quotationCustomerFilter,
  setQuotationCustomerFilter,
  quotationTypeFilter,
  setQuotationTypeFilter,
  quotationDateFrom,
  setQuotationDateFrom,
  quotationDateTo,
  setQuotationDateTo,
  viewQuotation,
  setViewQuotation,
  setEditQuotation,
  setTab,
  handleSaveQuotation,
  quotationSaving,
  handleDeleteQuotation,
  canManageQuotations,
  handlePrintQuotation,
  handlePrintTransportSlip,
  handleSendWhatsApp,
  handleSendUpiQr,
  formatDateTime,
  formatCurrency,
  quotationPage,
  quotationPageSize,
  quotationTotalPages,
  setQuotationPage,
  QuotationBuilder,
  quotationSettings,
  handleOpenCustomer,
}) {
  const filteredTotals = filteredQuotations.reduce(
    (acc, quotation) => {
      acc.pcs += (quotation.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const amount = Number(quotation.grand_total) || 0;
      acc.amount += amount;
      if (quotation.bill_number) {
        acc.billAmount += amount;
      } else {
        acc.quotationAmount += amount;
      }
      return acc;
    },
    { pcs: 0, amount: 0, billAmount: 0, quotationAmount: 0 }
  );
  
  const dateFilterFromDisplay = formatDateFilterDisplay(quotationDateFrom);
  const dateFilterToDisplay = formatDateFilterDisplay(quotationDateTo);
  const dateFilterMessage = getDateFilterMessage(dateFilterFromDisplay, dateFilterToDisplay);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <div>
          <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>{quotations.length} total quotations</p>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: B.textMid, fontWeight: 600, fontFamily: fonts.body }}>{dateFilterMessage}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={quotationSearch}
            onChange={e => setQuotationSearch(e.target.value)}
            placeholder="Search quotation no or customer..."
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 13px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", width: 230 }}
          />
          <select
            value={quotationCustomerFilter}
            onChange={e => setQuotationCustomerFilter(e.target.value)}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", minWidth: 180 }}
          >
            <option value="all">All customers</option>
            {customers.filter(c => c.status !== "deleted").map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.customer_name}
              </option>
            ))}
          </select>
          <select
            value={quotationTypeFilter}
            onChange={e => setQuotationTypeFilter(e.target.value)}
            style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", minWidth: 130 }}
          >
            <option value="all">All types</option>
            <option value="bills">Bills only</option>
            <option value="quotations">Quotations only</option>
          </select>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 10, color: B.textMid, fontFamily: fonts.body }}>From</span>
              <input
                type="date"
                value={quotationDateFrom}
                onChange={e => setQuotationDateFrom(e.target.value)}
                aria-label="Filter quotations from date"
                style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "7px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", minWidth: 140 }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 10, color: B.textMid, fontFamily: fonts.body }}>To</span>
              <input
                type="date"
                value={quotationDateTo}
                onChange={e => setQuotationDateTo(e.target.value)}
                aria-label="Filter quotations to date"
                style={{ background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 8, padding: "7px 10px", fontSize: 12, color: B.text, fontFamily: fonts.body, outline: "none", minWidth: 140 }}
              />
            </div>
            <span style={{ fontSize: 10, color: B.textLight, fontFamily: fonts.body, whiteSpace: "nowrap" }}>
              {!dateFilterFromDisplay && !dateFilterToDisplay ? "All dates" : ""}
            </span>
          </div>
        </div>
      </div>

      {viewQuotation && (
        <QuotationBuilder
          customers={customers}
          designs={designs}
          onSave={handleSaveQuotation}
          saving={quotationSaving}
          initialQuotation={viewQuotation}
          onCancelEdit={() => setViewQuotation(null)}
          readOnly
          quotationSettings={quotationSettings}
          quotations={quotations}
        />
      )}

      <div style={{ overflowX: "auto", background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 880 }}>
          <thead>
            <tr style={{ background: B.orangeFaint }}>
              {["Quotation #", "Bill #", "Customer Name", "Total Amount", "Pcs", "Mobile", "Created Date", "Actions"].map(h => (
                <th key={h} style={{ textAlign: "left", fontSize: 11, color: B.textMid, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "12px 10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedQuotations.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "24px", color: B.textLight, fontFamily: fonts.body }}>
                  No quotations found.
                </td>
              </tr>
            ) : paginatedQuotations.map(quotation => (
              <tr key={quotation.id}>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body, fontWeight: 700 }}>{quotation.quotation_number || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body, fontWeight: 700 }}>{quotation.bill_number || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>
                  {customers.some(c => c.id === quotation.customer_id) ? (
                    <button
                      type="button"
                      onClick={() => handleOpenCustomer(quotation.customer_id)}
                      title="View customer details"
                      style={{
                        border: "none", background: "transparent", cursor: "pointer",
                        fontFamily: fonts.body, fontSize: 13, padding: 0,
                        color: B.orange, textDecoration: "underline",
                      }}
                    >
                      {quotation.customer_name || "—"}
                    </button>
                  ) : (quotation.customer_name || "—")}
                </td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{formatCurrency(quotation.grand_total)}</td>
                <td style={{padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body}} >
                  {(quotation.items || []).reduce( (sum, item) => sum + (Number(item.quantity) || 0), 0)}
                </td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{quotation.customer_mobile || "—"}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}`, fontFamily: fonts.body }}>{formatDateTime(quotation.created_at)}</td>
                <td style={{ padding: "10px", borderBottom: `1px solid ${B.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button type="button" onClick={() => { setEditQuotation(null); setViewQuotation(quotation); }} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }} title="View">👁️</button>
                    <button type="button" onClick={() => { if (!canManageQuotations) return; setViewQuotation(null); setEditQuotation(quotation); setTab("Create Quotation"); window.scrollTo(0, 0); }} disabled={!canManageQuotations} style={{ border: "none", background: "transparent", cursor: canManageQuotations ? "pointer" : "not-allowed", fontSize: 16, opacity: canManageQuotations ? 1 : 0.35 }} title={canManageQuotations ? "Edit" : "No permission to edit"}>✏️</button>
                    <button type="button" onClick={() => handleDeleteQuotation(quotation)} disabled={!canManageQuotations} style={{ border: "none", background: "transparent", cursor: canManageQuotations ? "pointer" : "not-allowed", fontSize: 16, opacity: canManageQuotations ? 1 : 0.35 }} title={canManageQuotations ? "Delete" : "No permission to delete"}>🗑️</button>
                    <button type="button" onClick={() => handlePrintQuotation(quotation)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }} title="Print">🖨️</button>
                    <button
                      type="button"
                      onClick={() => handlePrintTransportSlip(quotation)}
                      disabled={!String(quotation.transport_name || "").trim()}
                      aria-label="Print transport slip"
                      style={{ border: "none", background: "transparent", cursor: quotation.transport_name ? "pointer" : "not-allowed", fontSize: 16, opacity: quotation.transport_name ? 1 : 0.35 }}
                      title={quotation.transport_name ? "Print transport slip" : "No transport name"}
                    >📦</button>
                    <button
                      type="button"
                      onClick={() => handleSendWhatsApp(quotation)}
                      disabled={!quotation.customer_mobile}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: quotation.customer_mobile ? "pointer" : "not-allowed",
                        fontSize: 16,
                        opacity: quotation.customer_mobile ? 1 : 0.35,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      title={quotation.customer_mobile ? "Send via WhatsApp" : "No customer mobile number to send WhatsApp"}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 2a10 10 0 00-8.7 15l-1.3 5 5.1-1.3A10 10 0 1012 2zm0 18a8 8 0 01-4.1-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8 8 0 1112 20zm4.3-5.7c-.2-.1-1.3-.6-1.5-.7s-.4-.1-.6.1-.7.7-.8.8-.3.1-.5 0a6.6 6.6 0 01-2-1.2 7.3 7.3 0 01-1.4-1.8c-.1-.2 0-.3.1-.4l.3-.3c.1-.1.2-.2.3-.4.1-.2 0-.3 0-.4s-.6-1.4-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.4.1-.6.3s-.8.8-.8 2 .8 2.4.9 2.6a9.8 9.8 0 003.8 3.6c.5.2.9.4 1.2.5.5.2 1 .2 1.4.1.4-.1 1.3-.5 1.5-1 .2-.5.2-.9.1-1-.1-.1-.3-.2-.5-.3z"/>
                      </svg>
                    </button>
                    {Boolean(quotation.bill_number) && (
                      <button
                        type="button"
                        onClick={() => handleSendUpiQr(quotation)}
                        disabled={!quotation.customer_mobile}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: quotation.customer_mobile ? "pointer" : "not-allowed",
                          fontSize: 16,
                          opacity: quotation.customer_mobile ? 1 : 0.35,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        title={quotation.customer_mobile ? `Send UPI payment QR for invoice #${quotation.bill_number}` : "No customer mobile number to send WhatsApp"}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#5c3a31" fillRule="evenodd">
                          <path d="M3 3h7v7H3V3zm2 2v3h3V5H5zm9-2h7v7h-7V3zm2 2v3h3V5h-3zM3 14h7v7H3v-7zm2 2v3h3v-3H5z"/>
                          <path d="M14 14h3v3h-3v-3zm4 0h3v3h-3v-3zm-4 4h3v3h-3v-3zm4 0h3v3h-3v-3z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredQuotations.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>
              Showing {(quotationPage - 1) * quotationPageSize + 1}-{Math.min(quotationPage * quotationPageSize, filteredQuotations.length)} of {filteredQuotations.length}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={() => setQuotationPage(p => Math.max(1, p - 1))} variant="ghost" small disabled={quotationPage === 1}>Previous</Btn>
              <span style={{ fontSize: 12, color: B.textMid, alignSelf: "center", fontFamily: fonts.body }}>Page {quotationPage} / {quotationTotalPages}</span>
              <Btn onClick={() => setQuotationPage(p => Math.min(quotationTotalPages, p + 1))} variant="ghost" small disabled={quotationPage === quotationTotalPages}>Next</Btn>
            </div>
          </div>

          <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: 18, background: B.orangeFaint, border: `1px solid ${B.borderLight}`, borderRadius: 10, padding: "10px 14px" }}>
              <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
                Total Pcs: <strong style={{ color: B.text }}>{filteredTotals.pcs}</strong>
              </span>
              <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
                Bill Amount: <strong style={{ color: B.text }}>{formatCurrency(filteredTotals.billAmount)}</strong>
              </span>
              <span style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
                Quotation Amount: <strong style={{ color: B.text }}>{formatCurrency(filteredTotals.quotationAmount)}</strong>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
